import { useEffect, useRef } from 'react';

/**
 * Recul as the whole screen (ruled 24 October: "it should become the full
 * interface of the app"), held in a frame from the same address so it needs
 * no sign-in of its own. Recul never uploads or signs in; it tells the app,
 * through the messages stage 3 built (public/recul/index.html, build 16a):
 *
 *   recul:ready            -> the app answers recul:context
 *   recul:swing {key,count} each swing, `count` that athlete's total this visit;
 *                          the key is empty when opened from Tools
 *   recul:save-in-lesson {key, clips: [{id, video, duration}]}
 *                          -> the app answers recul:saved {ids} once it has them
 *   recul:exit             -> leave
 *
 * Messages are taken only from Recul's own frame, from this address.
 */

export interface ReculAthlete {
  /** The lesson Recul's swings and clips go to. */
  key: string;
  name: string;
  /** Save in lesson is offered: the parent said Yes to photos and video. */
  canSave: boolean;
}

export interface ReculContext {
  /** e.g. "Today 5:00 PM"; empty when Recul is opened from Tools. */
  when: string;
  /** Everyone in the hour; two or more puts their names on the live screen. */
  athletes: ReculAthlete[];
  /** Who is selected when Recul opens. */
  current: string | null;
}

export interface ReculClip {
  id: string;
  video: Blob;
  duration: number;
}

export function ReculHost({
  context,
  onExit,
  onSwing,
  onPracticeSwing,
  onSave,
}: {
  context: ReculContext;
  onExit: () => void;
  onSwing?: (key: string, total: number) => void;
  /** A swing with no lesson: SwingReplay opened from Tools. */
  onPracticeSwing?: (total: number) => void;
  /** Resolves with the ids the app took. */
  onSave?: (key: string, clips: ReculClip[]) => string[];
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  // The latest handlers and context, without re-subscribing on every render.
  const latest = useRef({ context, onExit, onSwing, onPracticeSwing, onSave });
  latest.current = { context, onExit, onSwing, onPracticeSwing, onSave };

  useEffect(() => {
    const send = (message: unknown) => frame.current?.contentWindow?.postMessage(message, window.location.origin);
    const sendContext = () => send({ type: 'recul:context', ...latest.current.context });

    function heard(event: MessageEvent) {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      const message = (event.data ?? {}) as Record<string, unknown>;
      const { onExit: exit, onSwing: swing, onPracticeSwing: practiceSwing, onSave: save } = latest.current;
      if (message['type'] === 'recul:ready') sendContext();
      else if (message['type'] === 'recul:exit') exit();
      else if (message['type'] === 'recul:swing' && swing && typeof message['key'] === 'string' && message['key']) {
        swing(message['key'], Number(message['count']));
      } else if (message['type'] === 'recul:swing' && practiceSwing) {
        practiceSwing(Number(message['count']));
      } else if (message['type'] === 'recul:save-in-lesson' && save && typeof message['key'] === 'string') {
        const clips = (Array.isArray(message['clips']) ? message['clips'] : [])
          .filter((clip): clip is ReculClip => !!clip && typeof clip.id === 'string' && clip.video instanceof Blob)
          .map((clip) => ({ id: clip.id, video: clip.video, duration: Number(clip.duration) }));
        const ids = save(message['key'], clips);
        if (ids.length) send({ type: 'recul:saved', ids });
      }
    }

    window.addEventListener('message', heard);
    return () => window.removeEventListener('message', heard);
  }, []);

  // A context that changes while Recul is open (the day's list read again)
  // is sent again; Recul keeps who is selected.
  const contextKey = JSON.stringify(context);
  useEffect(() => {
    frame.current?.contentWindow?.postMessage({ type: 'recul:context', ...latest.current.context }, window.location.origin);
  }, [contextKey]);

  return (
    <iframe
      ref={frame}
      className="recul-frame"
      title="SwingReplay™"
      src="/recul/?app=1"
      allow="camera; microphone; screen-wake-lock"
    />
  );
}
